import axios from "axios";
import AuthStorage from "./AuthStorage";

class Client {

    constructor(context) {
        this.context = context;
    }

    /**
     * Post request
     * @param {string} uri
     * @param {Object} data
     * @returns {Promise<*>}
     */
    async post(uri, data) {
        if (!this.context.$isOnline()) {
            setTimeout(() => this.context.$routeTo('/offline'), 100);
            throw 'No internet connection';
        }

        try {
            let response = await axios.post(uri, data);

            return response.data.data;
        } catch (e) {
            await this.handleError(e, {
                method: 'POST',
                uri,
                payload: JSON.stringify(data)
            });
        }
    }

    /**
     * Post request
     * @param {string} uri
     * @returns {Promise<*>}
     */
    async get(uri) {
        if (!this.context.$isOnline()) {
            setTimeout(() => this.context.$routeTo('/offline'), 100);
            throw 'No internet connection';
        }

        try {
            let response = await axios.get(uri);

            return response.data.data;
        } catch (e) {
            await this.handleError(e,{
                method: 'GET',
                uri
            });
        }
    }

    /**
     * Authenticated get
     * @param {string} uri
     * @param {boolean} retry
     * @returns {Promise<*>}
     */
    async authGet(uri, retry = true) {
        /** @var {AxiosResponse} */
        let response;
        try {
            let token = AuthStorage.getApiToken();
            response = await this.get(`${uri}?api_token=${token}`);
        } catch (e) {
            return await this.handleAuthError(e, retry, () => {
                return this.authGet(uri, false);
            });
        }

        return response;
    }

    /**
     * Authenticated get
     * @param {string} uri
     * @param {Object} data
     * @param {boolean} retry
     * @returns {Promise<*>}
     */
    async authPost(uri, data, retry = true) {
        /** @var {AxiosResponse} */
        let response;
        try {
            let token = AuthStorage.getApiToken();
            response = await this.post(`${uri}?api_token=${token}`, data);
        } catch (e) {
            return await this.handleAuthError(e, retry, () => {
                return this.authPost(uri, data, false);
            });
        }

        return response;
    }

    /**
     * Handle common errors.
     *
     * @param {AxiosError} e
     * @param {Object} call
     * @returns {Promise<void>}
     */
    async handleError(e, call = {}) {
        if (!e.response) {
            this.context.$alert('Siamo spiacenti. Connessione ad internet precaria o server non raggiungibile, riprovare più tardi.');
        } else if (e.response.status >= 500) {
            this.context.$bugsnag.notify({
                error: e, metadata: {call}
            });
            this.context.$alert('Errore nella connessione al server! Si prega di riprovare.');
        } else if (![401, 403, 422].includes(e.response.status)) {
            this.context.$bugsnag.notify({
                error: e, metadata: {call}
            });
            this.context.$alert('Errore imprevisto. Si prega di riprovare');
        }

        throw e;
    }

    /**
     * Handle auth errors.
     *
     * @param {AxiosError} e
     * @param {boolean} retry
     * @param {function} retryCallback
     * @returns {Promise<void>}
     */
    async handleAuthError(e, retry, retryCallback) {
        // Try to refresh token if the error is permission related
        if (retry && e.response && [401, 403].includes(e.response.status)) {
            await this.context.$api.refreshToken();

            return await retryCallback();
        }
        throw e;
    }

}

export default Client;